import * as React from "react";
import { useTranslation } from "react-i18next";
import { Phone, MessageSquare, ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from "@/components/ui";
import { MobileNumberInput } from "./MobileNumberInput";
import { PinInput } from "./PinInput";
import { CountryCode, COUNTRY_CODES } from "./CountryCodeSelector";
import { motion, AnimatePresence } from "framer-motion";

export type RecoveryStep = "phone" | "otp" | "newPin" | "confirmPin" | "success";

export interface ForgotPinModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequestOtp: (phoneNumber: string) => Promise<boolean>;
  onVerifyOtp: (otp: string) => Promise<boolean>;
  onResetPin: (newPin: string) => Promise<boolean>;
  initialPhoneNumber?: string;
  initialCountry?: CountryCode;
}

export const ForgotPinModal: React.FC<ForgotPinModalProps> = ({
  open,
  onOpenChange,
  onRequestOtp,
  onVerifyOtp,
  onResetPin,
  initialPhoneNumber = "",
  initialCountry = COUNTRY_CODES[0],
}) => {
  const { t } = useTranslation();
  const [step, setStep] = React.useState<RecoveryStep>("phone");
  const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber);
  const [countryCode, setCountryCode] = React.useState<CountryCode>(initialCountry);
  const [otp, setOtp] = React.useState("");
  const [newPin, setNewPin] = React.useState("");
  const [confirmPin, setConfirmPin] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setStep("phone");
      setOtp("");
      setNewPin("");
      setConfirmPin("");
      setError("");
    }
  }, [open]);

  const handleRequestOtp = async () => {
    if (phoneNumber.length < 8) {
      setError(t("auth.invalidPhone", "Please enter a valid phone number"));
      return;
    }

    setLoading(true);
    setError("");

    try {
      const fullNumber = `${countryCode.dialCode}${phoneNumber}`;
      const success = await onRequestOtp(fullNumber);
      if (success) {
        setStep("otp");
      } else {
        setError(t("auth.otpFailed", "Failed to send OTP. Please try again."));
      }
    } catch {
      setError(t("auth.otpFailed", "Failed to send OTP. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    setLoading(true);
    setError("");

    try {
      const success = await onVerifyOtp(code);
      if (success) {
        setStep("newPin");
      } else {
        setError(t("auth.invalidOtp", "Invalid code. Please try again."));
        setOtp("");
      }
    } catch {
      setError(t("auth.invalidOtp", "Invalid code. Please try again."));
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPinComplete = (pin: string) => {
    setNewPin(pin);
    setStep("confirmPin");
  };

  const handleConfirmPinComplete = async (pin: string) => {
    if (pin !== newPin) {
      setError(t("auth.pinMismatch", "PINs don't match. Please try again."));
      setConfirmPin("");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const success = await onResetPin(pin);
      if (success) {
        setStep("success");
        setTimeout(() => {
          onOpenChange(false);
        }, 2000);
      } else {
        setError(t("auth.resetFailed", "Failed to reset PIN. Please try again."));
      }
    } catch {
      setError(t("auth.resetFailed", "Failed to reset PIN. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "phone":
        return (
          <motion.div
            key="phone"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone className="w-8 h-8 text-primary" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">
                {t("auth.recoverAccount", "Recover Your Account")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("auth.enterPhoneToRecover", "Enter your registered phone number to receive a verification code")}
              </p>
            </div>

            <MobileNumberInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              countryCode={countryCode}
              onCountryChange={setCountryCode}
              error={error}
              autoFocus
            />

            <Button
              onClick={handleRequestOtp}
              disabled={phoneNumber.length < 8 || loading}
              className="w-full h-14 text-lg"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {t("auth.sendCode", "Send Code")}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </motion.div>
        );

      case "otp":
        return (
          <motion.div
            key="otp"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-primary" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">
                {t("auth.enterCode", "Enter Verification Code")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("auth.codeSentTo", "Code sent to")} {countryCode.dialCode} {phoneNumber}
              </p>
            </div>

            <PinInput
              value={otp}
              onChange={setOtp}
              onComplete={handleVerifyOtp}
              error={!!error}
              errorMessage={error}
              disabled={loading}
              autoFocus
            />

            <Button
              variant="ghost"
              onClick={() => setStep("phone")}
              className="w-full"
            >
              {t("auth.changeNumber", "Change phone number")}
            </Button>
          </motion.div>
        );

      case "newPin":
        return (
          <motion.div
            key="newPin"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">
                {t("auth.createNewPin", "Create New PIN")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("auth.enter4DigitPin", "Enter a 4-digit PIN you'll remember")}
              </p>
            </div>

            <PinInput
              value={newPin}
              onChange={setNewPin}
              onComplete={handleNewPinComplete}
              disabled={loading}
              autoFocus
            />
          </motion.div>
        );

      case "confirmPin":
        return (
          <motion.div
            key="confirmPin"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">
                {t("auth.confirmPin", "Confirm Your PIN")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t("auth.reenterPin", "Re-enter your new PIN to confirm")}
              </p>
            </div>

            <PinInput
              value={confirmPin}
              onChange={setConfirmPin}
              onComplete={handleConfirmPinComplete}
              error={!!error}
              errorMessage={error}
              disabled={loading}
              autoFocus
            />
          </motion.div>
        );

      case "success":
        return (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-6 py-8"
          >
            <div className="flex justify-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center"
              >
                <motion.div
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <svg className="w-10 h-10 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <motion.path
                      d="M5 13l4 4L19 7"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.2, duration: 0.5 }}
                    />
                  </svg>
                </motion.div>
              </motion.div>
            </div>

            <div className="text-center">
              <h3 className="text-lg font-semibold text-green-600">
                {t("auth.pinReset", "PIN Reset Successfully!")}
              </h3>
            </div>
          </motion.div>
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="sr-only">
          <DialogTitle>{t("auth.forgotPin", "Forgot PIN")}</DialogTitle>
          <DialogDescription>
            {t("auth.recoverAccountDesc", "Recover your account using your phone number")}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPinModal;
